import { make } from 'vuex-pathify'
import router from '@/router'
import { login, refreshTokenApi } from '@/services/auth.service'
import { isValidToken, getTimeDiff, decodeJWT } from '@/util/jwt-util'
import { createLogger } from '@/utils/logger'

const logger = createLogger('auth')

// For testing: artificially shorten perceived token lifetime so refresh happens sooner.
// Set to small value (e.g. 2 minutes) while debugging token refresh issues.
// Change this to a larger value or 0 to disable artificial shortening.
const ARTIFICIAL_MAX_VALIDITY_MS = 2 * 60 * 1000 // 2 minutes
// Buffer before expiry to trigger proactive refresh (default 5s)
const RENEWAL_TIME_BUFFER_MS = 5000
const state = {
  authorized: false,
  jwt: null,
  isRefreshing: false,
  refreshPromise: null,
  refreshTimeoutId: null,
}

const mutations = make.mutations(state)

const getters = {
  isAuthorized: state => state.authorized,
  expire: state => state.jwt && state.jwt.exp,
  accessToken: state => state.jwt && state.jwt.access,
  isRefreshing: state => state.isRefreshing,
  refreshPromise: state => state.refreshPromise,
}

function clearTokens(commit) {
  logger.info('Clearing tokens from store and localStorage')
  commit('authorized', false)
  commit('jwt', null)
  try {
    localStorage.removeItem('jwt')
  } catch (e) {
    logger.error('Failed to remove jwt from localStorage', e.message || e)
  }
}

function loadTokens(state, commit) {
  try {
    const raw = localStorage.getItem('jwt')
    const jwt = raw ? JSON.parse(raw) : null
    commit('jwt', jwt)
    logger.debug('Loaded tokens from localStorage', jwt)
  } catch (e) {
    logger.error('Failed to load jwt from localStorage', e.message || e)
    commit('jwt', null)
  }
}

function updateTokens(state, commit, tokens) {
  const decodeAccessToken = decodeJWT(tokens && tokens.access)
  const jwt = {
    access:  tokens && (tokens.access || ''),
    refresh: tokens && (tokens.refresh || ''),
    sub: decodeAccessToken && (decodeAccessToken.sub || ''),
    rol: decodeAccessToken && (decodeAccessToken.rol || ''),
    iat: decodeAccessToken && (decodeAccessToken.iat || ''),
    exp: decodeAccessToken && (decodeAccessToken.exp || '')
  }
  commit('jwt', jwt)
  try {
    localStorage.setItem('jwt', JSON.stringify(jwt))
    logger.debug('Updated tokens and persisted to localStorage', { exp: jwt.exp, sub: jwt.sub })
  } catch (e) {
    logger.error('Failed to persist jwt to localStorage', e.message || e)
  }
}

function getEffectiveTimeDiff(exp) {
  const timeDiff = getTimeDiff(exp)
  if (timeDiff === false) return false
  // Respect artificial max validity when set (>0)
  if (ARTIFICIAL_MAX_VALIDITY_MS && ARTIFICIAL_MAX_VALIDITY_MS > 0) {
    return Math.min(timeDiff, ARTIFICIAL_MAX_VALIDITY_MS)
  }
  return timeDiff
}

function authorize(state, commit, dispatch, tokens) {
  logger.debug('authorize', tokens);
  if (!tokens) {
    return
  }
  if (isValidToken(tokens && tokens.access)) {
    updateTokens(state, commit, tokens)
    commit('authorized', true)
    dispatch('refreshToken')
  } else if(isValidToken(tokens && tokens.refresh)) {
    // If access token invalid but refresh token valid, update tokens first then refresh
    updateTokens(state, commit, tokens)
    commit('authorized', true)
    // Refresh immediately since access token is invalid
    fetchNewAccessToken(state, commit, dispatch)
  } else {
    logger.warn('Token validation failed')
    // Only logout if we were previously authorized and tokens were provided
    if (state.authorized && tokens) {
      dispatch('logout')
    }
  }
}

function fetchNewAccessToken(state, commit, dispatch ) {
  // If already refreshing, return existing promise
  if (state.isRefreshing && state.refreshPromise) {
    logger.debug('Token refresh already in progress, waiting...', { hasPromise: !!state.refreshPromise })
    return state.refreshPromise
  }

  if (!state.jwt?.refresh) {
    logger.error('No refresh token available')
    // Clear state before logout
    commit('isRefreshing', false)
    commit('refreshPromise', null)
    // Clear tokens immediately (synchronous)
    clearTokens(commit)
    // Then dispatch logout to handle navigation
    dispatch('logout')
    return Promise.reject(new Error('No refresh token'))
  }

  logger.debug('Starting new token refresh')
  commit('isRefreshing', true)

  // Create the promise and store it BEFORE making the API call
  // This ensures concurrent calls will reuse this promise
  const refreshPromise = refreshTokenApi(state.jwt.refresh)
    .then(async response => {
      logger.debug('Refresh API response', response && response.data ? Object.keys(response.data) : response)
      const data = {
        access: response.data.access,
        refresh: state.jwt.refresh
      }
      if (response.data.refresh) {
        data.refresh = response.data.refresh
      }
      logger.info('Token refreshed successfully')
      authorize(state, commit, dispatch, data)
      return data
    })
    .catch(error => {
      logger.error('Token refresh failed:', error.message || error)
      // Clear state before logout to prevent hanging
      commit('isRefreshing', false)
      commit('refreshPromise', null)
      // Clear tokens immediately (synchronous)
      clearTokens(commit)
      // Then dispatch logout to handle navigation
      dispatch('logout')
      throw error
    })
    .finally(() => {
      // Always clear these flags, even on success
      commit('isRefreshing', false)
      commit('refreshPromise', null)
    })

  // Store the promise so concurrent calls can reuse it
  commit('refreshPromise', refreshPromise)
  return refreshPromise
}

const actions = {
  init: async ({ state, commit, dispatch }) => {
    loadTokens(state, commit)
    authorize(state, commit, dispatch, state.jwt)

    // Handle page visibility changes (e.g., returning from sleep/switching tabs)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        logger.info('Tab became visible, checking token validity')

        // Only check if user was previously authorized
        if (!state.authorized) {
          logger.debug('User not authorized, skipping token check')
          return
        }

        loadTokens(state, commit) // Reload from localStorage in case it changed

        // If we have a token, check if it needs refresh
        if (state.jwt?.access) {
          const rawTimeDiff = getTimeDiff(state.jwt.exp)
          const timeDiff = getEffectiveTimeDiff(state.jwt.exp)
          logger.debug('Visibility check token time', { rawTimeDiff, effectiveTimeDiff: timeDiff })
          // If token expires within our renewal buffer or already expired, refresh immediately
          if (timeDiff === false || timeDiff < RENEWAL_TIME_BUFFER_MS) {
            logger.info('Token expired or expiring soon, refreshing immediately')
            fetchNewAccessToken(state, commit, dispatch)
          }
        } else {
          // No token at all, logout
          logger.warn('No token found after visibility change')
          dispatch('logout')
        }
      }
    })

    // Handle network reconnection
    window.addEventListener('online', () => {
      logger.info('Network connection restored')

      // Only check if user was previously authorized
      if (!state.authorized) {
        logger.debug('User not authorized, skipping token check')
        return
      }

      loadTokens(state, commit)

      // Check if we still have valid tokens after coming back online
      if (state.jwt?.access) {
        const rawTimeDiff = getTimeDiff(state.jwt.exp)
        const timeDiff = getEffectiveTimeDiff(state.jwt.exp)
        logger.debug('Online check token time', { rawTimeDiff, effectiveTimeDiff: timeDiff })
        // If token expired while offline, refresh immediately
        if (timeDiff === false || timeDiff <= 0) {
          logger.info('Token expired while offline, refreshing')
          fetchNewAccessToken(state, commit, dispatch)
        } else if (timeDiff < RENEWAL_TIME_BUFFER_MS) {
          logger.info('Token expiring soon, refreshing proactively')
          fetchNewAccessToken(state, commit, dispatch)
        }
      } else {
        logger.warn('No valid token after reconnection')
        dispatch('logout')
      }
    })

    // Handle going offline
    window.addEventListener('offline', () => {
      logger.info('Network connection lost')
      // Don't logout immediately - user might come back online
      // Just log the event for debugging
    })
  },

  refreshToken: ({ state, commit, dispatch }) => {
    const rawTimeDiff = getTimeDiff(state.jwt && state.jwt.exp)
    const timeDiff = getEffectiveTimeDiff(state.jwt && state.jwt.exp)
    if (timeDiff === false) {
      logger.warn('No expiration on token; not scheduling refresh')
      return Promise.resolve()
    }

    // Cancel existing scheduled refresh if present
    if (state.refreshTimeoutId) {
      try { clearTimeout(state.refreshTimeoutId) } catch (e) { /* ignore */ }
      commit('refreshTimeoutId', null)
    }

    const timeoutCount = RENEWAL_TIME_BUFFER_MS < timeDiff ? timeDiff - RENEWAL_TIME_BUFFER_MS : timeDiff
    logger.debug('Scheduling token refresh', { rawTimeDiff, effectiveTimeDiff: timeDiff, timeoutCount })

    if (timeoutCount > 0) {
      // Schedule refresh for later and keep id for debugging
      const timeoutId = setTimeout(() => {
        logger.debug('Triggered scheduled token refresh')
        fetchNewAccessToken(state, commit, dispatch)
      }, timeoutCount)
      commit('refreshTimeoutId', timeoutId)
      // Return resolved promise since we're just scheduling
      return Promise.resolve()
    } else {
      // Refresh immediately and return the promise
      logger.debug('TimeoutCount <= 0; refreshing immediately')
      return fetchNewAccessToken(state, commit, dispatch)
    }
  },

  authenticationUser: ({ state, commit, dispatch }, { email, password }) => {
    return login(email, password)
      .then(response => {
        authorize(state, commit, dispatch, response.data)
        // Wait a tick to ensure state is updated before fetching user
        return new Promise(resolve => setTimeout(resolve, 0))
      })
      .then(() => {
        return dispatch('user/fetchUser', null, { root: true })
      })
  },

  logout: async ({ commit, dispatch }) => {
    clearTokens(commit)
    dispatch('user/clear', null, {root: true})
    dispatch('dashboard/reset', null, {root: true})
    if (router) { //init is before router
      router.push({ name: 'SignIn' }).catch(() => {});
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
